import Editor, { type BeforeMount, type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { useResolvedTheme } from '../../../lib/theme';
import type { RepoBrowserEditorSelection } from '../hooks/useRepoBrowser';
import type { RepoBrowserFileDTO, RepoBrowserNodeKind } from '../types/source-system';

const defineProtossThemes: BeforeMount = (monaco) => {
  // Guard against StrictMode double-invocation - defineTheme is not idempotent
  // across hot reloads, so bail early if already registered.
  const registered =
    monaco.languages.getLanguages().length > 0 &&
    (
      monaco.editor as unknown as { _themeService?: { _knownThemes?: Map<string, unknown> } }
    )._themeService?._knownThemes?.has('protoss-light');
  if (registered) return;

  monaco.editor.defineTheme('protoss-light', {
    base: 'vs',
    inherit: true,
    rules: [],
    colors: {
      'editor.background': '#f9fafb',
      'editor.foreground': '#111827',
      'editorLineNumber.foreground': '#9ca3af',
      'editorLineNumber.activeForeground': '#6b7280',
      'editor.lineHighlightBackground': '#f3f4f600',
      'editor.lineHighlightBorder': '#f3f4f6',
      'editor.selectionBackground': '#dbeafe',
      'editorCursor.foreground': '#3b82f6',
      'editorIndentGuide.background1': '#e5e7eb',
      'editorIndentGuide.activeBackground1': '#d1d5db',
    },
  });

  monaco.editor.defineTheme('protoss-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [],
    colors: {
      'editor.background': '#1f2937',
      'editor.foreground': '#f9fafb',
      'editorLineNumber.foreground': '#9ca3af',
      'editorLineNumber.activeForeground': '#d1d5db',
      'editor.lineHighlightBackground': '#37415100',
      'editor.lineHighlightBorder': '#374151',
      'editor.selectionBackground': '#1d4ed840',
      'editorCursor.foreground': '#3b82f6',
      'editorIndentGuide.background1': '#374151',
      'editorIndentGuide.activeBackground1': '#4b5563',
    },
  });
};

function useMonacoTheme(): 'protoss-dark' | 'protoss-light' {
  return useResolvedTheme() === 'dark' ? 'protoss-dark' : 'protoss-light';
}

interface RepoFileViewerProps {
  file: RepoBrowserFileDTO | null;
  loading: boolean;
  error: string | null;
  selectedNodeKind: RepoBrowserNodeKind | null;
  onSelectionChange: (selection: RepoBrowserEditorSelection | null, selectedText: string) => void;
  onRetry?: () => void;
}

export function RepoFileViewer({
  file,
  loading,
  error,
  selectedNodeKind,
  onSelectionChange,
  onRetry,
}: RepoFileViewerProps) {
  const monacoTheme = useMonacoTheme();
  const selectionDisposableRef = React.useRef<{ dispose(): void } | null>(null);

  const handleMount = React.useCallback<OnMount>(
    (monacoEditor: editor.IStandaloneCodeEditor) => {
      selectionDisposableRef.current?.dispose();
      selectionDisposableRef.current = monacoEditor.onDidChangeCursorSelection(() => {
        const selection = monacoEditor.getSelection();
        const model = monacoEditor.getModel();
        if (!selection || !model) {
          onSelectionChange(null, '');
          return;
        }

        const selectedText = model.getValueInRange(selection);
        onSelectionChange(
          {
            startLineNumber: selection.startLineNumber,
            startColumn: selection.startColumn,
            endLineNumber: selection.endLineNumber,
            endColumn: selection.endColumn,
          },
          selectedText
        );
      });
    },
    [onSelectionChange]
  );

  React.useEffect(() => {
    return () => {
      selectionDisposableRef.current?.dispose();
      selectionDisposableRef.current = null;
    };
  }, []);

  if (loading) {
    return <div className="ss-browser-viewer__empty">Loading file…</div>;
  }

  if (error) {
    return (
      <div className="ss-browser-viewer__empty ss-browser-viewer__empty--error">
        {onRetry ? <PageErrorState message={error} onRetry={onRetry} /> : error}
      </div>
    );
  }

  if (!file) {
    return (
      <div className="ss-browser-viewer__empty">
        {selectedNodeKind === 'directory'
          ? 'Directory selected. Choose a file to open its stored contents.'
          : selectedNodeKind === 'repo'
            ? 'Repository selected. Choose a file from the tree to inspect it.'
            : 'Select a file to inspect the ingested snapshot.'}
      </div>
    );
  }

  return (
    <div className="ss-browser-viewer">
      <div className="ss-browser-viewer__editor">
        <Editor
          height="100%"
          width="100%"
          beforeMount={defineProtossThemes}
          path={file.nodeId}
          language={file.languageHint ?? 'plaintext'}
          value={file.content}
          onMount={handleMount}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            lineNumbersMinChars: 3,
            tabSize: 2,
            wordWrap: 'on',
            glyphMargin: false,
            folding: true,
            renderLineHighlight: 'line',
            unicodeHighlight: {
              ambiguousCharacters: false,
              invisibleCharacters: false,
              nonBasicASCII: false,
            },
          }}
          theme={monacoTheme}
        />
      </div>
    </div>
  );
}
